// src/modals/CSOTDetailsModal.jsx
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts';
import BaseExpandedCard from '../components/cards/BaseExpandedCard';
import ProjectNameLink from '../components/common/ProjectNameLink';
import ClientNameLink from '../components/common/ClientNameLink';
import { LoadingState, ErrorState, EmptyState, StatCard, ScoreChip } from '../components/common';
import { useModalData } from '../hooks/useModalData';
import { getCsotColor, getHoursColor } from '../utils/colorHelpers';
import { standardizeEmployeeId, cleanJsonString } from '../utils/formatters';
import { API_BASE_URL } from '../config';
import { CURRENT_DATE } from '../utils/constants';
import MetricCopyright from '../components/common/MetricCopyright';

// Palette colors — one per project, cycles if more than 8 projects
const PROJECT_COLORS = [
  '#03DAC6', // teal
  '#FFCF44', // yellow
  '#BB86FC', // purple
  '#64B5F6', // light blue
  '#CF6679', // rose
  '#4DB6AC', // light teal
  '#FFB74D', // amber
  '#81C784', // light green
];

const isConfirmedAssignment = (a) =>
  !String(a.status || '').toUpperCase().includes('SOFT');

const CSOTDetailsModal = ({ open, onClose, employeeName, employee_id, tabMode = false }) => {
  const fullEmployeeId = useMemo(() => standardizeEmployeeId(employee_id), [employee_id]);
  const [view, setView] = useState('planned'); // 'planned' | 'confirmed'

  const { data: csotData, loading, error } = useModalData(
    `${API_BASE_URL}/csot/${fullEmployeeId}?include_details=true`,
    open,
    {
      cache: true,
      ttl: 5 * 60 * 1000,
      transform: (responseText) => {
        const cleanedJson = cleanJsonString(typeof responseText === 'string' ? responseText : JSON.stringify(responseText));
        return typeof responseText === 'string' ? JSON.parse(cleanedJson) : responseText;
      }
    }
  );

  const csotRaw = csotData?.metrics?.csot;

  const csotMetrics = useMemo(() => {
    const confirmed = view === 'confirmed';
    return {
      csot_4_weeks:  confirmed ? (csotRaw?.csot_4_weeks_confirmed  ?? csotRaw?.csot_4_weeks  ?? 0) : (csotRaw?.csot_4_weeks  ?? 0),
      csot_8_weeks:  confirmed ? (csotRaw?.csot_8_weeks_confirmed  ?? csotRaw?.csot_8_weeks  ?? 0) : (csotRaw?.csot_8_weeks  ?? 0),
      csot_12_weeks: confirmed ? (csotRaw?.csot_12_weeks_confirmed ?? csotRaw?.csot_12_weeks ?? 0) : (csotRaw?.csot_12_weeks ?? 0),
    };
  }, [csotRaw, view]);

  const assignments = useMemo(() => csotData?.metrics?.assignments || [], [csotData]);

  const parseDate = (dateString) => {
    if (!dateString) return null;
    const [month, day, year] = dateString.split('/');
    return new Date(year, month - 1, day, 12, 0, 0);
  };

  // Filter by view: confirmed excludes SOFT_BOOKED assignments
  const filteredAssignments = useMemo(() =>
    view === 'confirmed' ? assignments.filter(isConfirmedAssignment) : assignments,
  [assignments, view]);

  const sortedAssignments = useMemo(() => {
    return [...filteredAssignments].sort((a, b) => {
      const dateA = parseDate(a.start_date);
      const dateB = parseDate(b.start_date);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA - dateB; // ascending: oldest first
    });
  }, [filteredAssignments]); // eslint-disable-line react-hooks/exhaustive-deps

  const countWorkDays = (startDate, endDate) => {
    let workDays = 0;
    const current = new Date(startDate.getTime());
    const end = new Date(endDate.getTime());
    current.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    while (current <= end) {
      const dow = current.getDay();
      if (dow >= 1 && dow <= 5) workDays++;
      current.setDate(current.getDate() + 1);
    }
    return workDays;
  };

  // Build unique project list (in assignment order) and assign colors
  const projectList = useMemo(() => {
    const seen = new Set();
    const list = [];
    filteredAssignments.forEach(a => {
      const key = a.project_name || a.project_id || 'Unknown';
      if (!seen.has(key)) {
        seen.add(key);
        list.push({
          key,
          name: a.project_name || key,
          client_name: a.client_name,
          status: a.status,
        });
      }
    });
    return list.map((p, i) => ({ ...p, color: PROJECT_COLORS[i % PROJECT_COLORS.length] }));
  }, [filteredAssignments]);

  // Color lookup by project key
  const projectColorMap = useMemo(() => {
    const map = {};
    projectList.forEach(p => { map[p.key] = p.color; });
    return map;
  }, [projectList]);

  // Build per-project weekly forecast
  const weeklyForecast = useMemo(() => {
    if (!filteredAssignments.length || !projectList.length) return [];

    const getNextFriday = (date) => {
      const d = new Date(date);
      const day = d.getDay();
      const daysUntilFriday = (5 - day + 7) % 7 || 7;
      d.setDate(d.getDate() + daysUntilFriday);
      return d;
    };

    const startFriday = getNextFriday(CURRENT_DATE);
    const weeks = [];

    for (let i = 0; i < 26; i++) {
      const weekEnd = new Date(startFriday);
      weekEnd.setDate(startFriday.getDate() + i * 7);
      const weekStart = new Date(weekEnd);
      weekStart.setDate(weekEnd.getDate() - 6);

      const weekData = {
        weekEnd: weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        weekEndDate: weekEnd,
      };

      // Initialise each project to 0
      projectList.forEach(p => { weekData[p.key] = 0; });

      filteredAssignments.forEach(assignment => {
        const key = assignment.project_name || assignment.project_id || 'Unknown';
        const assignmentStart = parseDate(assignment.start_date);
        const assignmentEnd = parseDate(assignment.end_date);
        if (!assignmentStart || !assignmentEnd) return;
        if (assignmentStart > weekEnd || assignmentEnd < weekStart) return;

        const weeklyHours = assignment.avg_hrs_week || 0;
        const isStartingWeek = assignmentStart >= weekStart && assignmentStart <= weekEnd;
        const isEndingWeek = assignmentEnd >= weekStart && assignmentEnd <= weekEnd;

        let hours = weeklyHours;
        if (isStartingWeek && isEndingWeek) {
          hours = (countWorkDays(assignmentStart, assignmentEnd) / 5.0) * weeklyHours;
        } else if (isEndingWeek) {
          hours = (countWorkDays(weekStart, assignmentEnd) / 5.0) * weeklyHours;
        }

        weekData[key] = Math.round(((weekData[key] || 0) + hours) * 10) / 10;
      });

      weeks.push(weekData);
    }

    return weeks;
  }, [filteredAssignments, projectList]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = (
    <><MetricCopyright name="Capacity Sold Over Time" />: {employeeName}</>
  );

  const Wrap = ({ children }) => tabMode
    ? <>{children}</>
    : <BaseExpandedCard open={open} onClose={onClose} title={title} subtitle="Scheduled work as a percentage of available hours">{children}</BaseExpandedCard>;

  if (loading) return <Wrap><LoadingState message="Fetching CSOT data..." /></Wrap>;
  if (error) return <Wrap><ErrorState message={error} /></Wrap>;

  return (
    <Wrap>

      {/* View Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, val) => { if (val) setView(val); }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: 'text.secondary',
              borderColor: '#424242',
              px: 2,
              fontSize: '0.75rem',
              textTransform: 'none',
            },
            '& .MuiToggleButton-root.Mui-selected': {
              color: '#03DAC6',
              borderColor: '#03DAC6',
              bgcolor: 'rgba(3,218,198,0.1)',
              '&:hover': { bgcolor: 'rgba(3,218,198,0.15)' },
            },
          }}
        >
          <ToggleButton value="planned">Planned</ToggleButton>
          <ToggleButton value="confirmed">Confirmed</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* CSOT Summary */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: '4-Week CSOT', value: csotMetrics.csot_4_weeks },
          { label: '8-Week CSOT', value: csotMetrics.csot_8_weeks },
          { label: '12-Week CSOT', value: csotMetrics.csot_12_weeks },
        ].map(({ label, value }) => (
          <StatCard key={label} label={<>{label}<sup style={{ fontSize: '0.55em' }}>©</sup></>}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: getCsotColor(value), lineHeight: 1 }}>
              {Math.round(value)}%
            </Typography>
          </StatCard>
        ))}
      </Box>

      {/* Weekly Billable Hours Forecast — broken out by project */}
      {weeklyForecast.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            26-Week Billable Hours Forecast
            {view === 'confirmed' && (
              <Typography component="span" variant="caption" sx={{ ml: 1, color: '#03DAC6' }}>
                (Confirmed only)
              </Typography>
            )}
          </Typography>
          <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={weeklyForecast} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#424242" />
                <XAxis
                  dataKey="weekEnd"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fill: '#9e9e9e', fontSize: 11 }}
                />
                <YAxis
                  domain={[0, 50]}
                  ticks={[0, 10, 20, 30, 40, 50]}
                  tick={{ fill: '#9e9e9e' }}
                  label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { fill: '#9e9e9e', fontSize: 12 } }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid #424242', borderRadius: '4px' }}
                  labelStyle={{ color: '#fff', fontWeight: 'bold' }}
                  formatter={(value, name) => [`${value} hrs`, name]}
                />
                <ReferenceLine
                  y={40}
                  stroke="#03DAC6"
                  strokeWidth={2}
                  strokeDasharray="8 4"
                  label={{ value: '40h capacity', position: 'insideTopLeft', fill: '#03DAC6', fontSize: 11, fontWeight: 'bold' }}
                />
                {projectList.map((project, i) => (
                  <Bar
                    key={project.key}
                    dataKey={project.key}
                    stackId="hours"
                    fill={project.color}
                    name={`${project.client_name} — ${project.name}`}
                    radius={i === projectList.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>

            {/* Legend */}
            <Box sx={{ mt: 2, display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              {projectList.map(project => (
                <Box key={project.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: project.color, borderRadius: '50%', flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary">
                    <ClientNameLink client={{ client_name: project.client_name }}>{project.client_name}</ClientNameLink>
                    {' — '}
                    <ProjectNameLink project={{ project_id: project.project_id, name: project.name, client_name: project.client_name }}>{project.name}</ProjectNameLink>
                  </Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 2, bgcolor: '#03DAC6', borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">40-Hour Target</Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Assignments Table */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Current & Upcoming Assignments
        {view === 'confirmed' && (
          <Typography component="span" variant="caption" sx={{ ml: 1, color: '#03DAC6' }}>
            (Confirmed only — soft bookings hidden)
          </Typography>
        )}
      </Typography>

      {sortedAssignments.length > 0 ? (
        <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell align="right">Avg Hrs/Week</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAssignments.map((assignment, index) => {
                const hoursPerWeek = assignment.avg_hrs_week || 40;
                const projectKey = assignment.project_name || assignment.project_id || 'Unknown';
                const dotColor = projectColorMap[projectKey] || '#888';

                return (
                  <TableRow key={index} hover>
                    <TableCell sx={{ color: '#64B5F6' }}>
                      <ClientNameLink client={{ client_name: assignment.client_name }}>{assignment.client_name || 'Unknown'}</ClientNameLink>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
                        <ProjectNameLink project={{ project_id: assignment.project_id, name: assignment.project_name, client_name: assignment.client_name }}>
                          {assignment.project_name || 'Unknown'}
                        </ProjectNameLink>
                      </Box>
                    </TableCell>
                    <TableCell>{assignment.role_name || 'Team Member'}</TableCell>
                    <TableCell>
                      {assignment.status ? (
                        <ScoreChip
                          value={assignment.status}
                          color={assignment.status.toUpperCase().includes('SOFT') ? '#FFCF44' : '#03DAC6'}
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell>{assignment.start_date || 'N/A'}</TableCell>
                    <TableCell>{assignment.end_date || 'N/A'}</TableCell>
                    <TableCell align="right">
                      <Typography component="span" sx={{ fontWeight: 'bold', color: getHoursColor(hoursPerWeek) }}>
                        {hoursPerWeek}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState
          title={view === 'confirmed' ? 'No confirmed assignments found' : 'No current or upcoming assignments found'}
          message={view === 'confirmed' ? 'All assignments for this employee are soft bookings' : 'This employee has no scheduled project assignments'}
        />
      )}

      {/* Last Updated */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Last updated:{' '}
          {csotData?.metrics?.csot?.last_updated
            ? new Date(csotData.metrics.csot.last_updated).toLocaleString()
            : 'Unknown'}
        </Typography>
      </Box>
    </Wrap>
  );
};

CSOTDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  employeeName: PropTypes.string.isRequired,
  employee_id: PropTypes.string.isRequired
};

export default CSOTDetailsModal;
